import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDarkMode } from "../Functions/DarkMode";
import { handleScroll } from "../Functions/ScrollUtils";
import { getAllProperties, propertiesToArray } from "../Functions/FetchProperties";
import '../assets/css/styles.css'

const Header = ({ currentPage = 'home' }) => {
    const { darkMode, toggleDarkMode } = useDarkMode();
    const [isScrolled, setIsScrolled] = useState(false);
    const isAdminPage = currentPage === 'admin'; // Detect admin page
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false); // New state for menu toggle

    // Add scrollToTop function like in Footer component
    const scrollToTop = () => {
        window.scrollTo(0, 0);
    };

    // Handle both menu toggle and scroll to top
    const handleNavLinkClick = () => {
        setIsMenuOpen(false);
        scrollToTop();
    };

    useEffect(() => {
        // Set initial scroll class
        handleScroll();
        
        // Add event listener
        window.addEventListener('scroll', handleScroll);
        
        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Fetch locations for dropdown menu
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setLoading(true);
                const propertiesData = await getAllProperties();
                const propertiesArray = propertiesToArray(propertiesData);
                
                // Extract unique locations
                const locationSet = new Set();
                
                propertiesArray.forEach(prop => {
                    if (prop.location) {
                        if (prop.location.toLowerCase().includes('pattaya') || 
                            prop.location.toLowerCase().includes('thailand')) {
                            locationSet.add('Pattaya, Thailand');
                        } else {
                            locationSet.add(prop.location);
                        }
                    }
                });
                
                setLocations([...locationSet].sort());
            } catch (error) {
                console.error("Error fetching locations:", error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchLocations();
    }, []);

    // Determine text color class based on scroll, dark mode, and page
    const getTextColorClass = () => {
        // Admin page in light mode - always black text regardless of scroll position
        if (isAdminPage && !darkMode) {
            return 'text-dark-content';
        }
        
        // Content pages (About, Contact, Properties) should have dark text in light mode when not scrolled
        if (!darkMode && !isScrolled && (currentPage === 'about' || currentPage === 'contact' || currentPage === 'properties')) {
            return 'text-dark-content';
        }
        
        // If not scrolled, always use white text regardless of mode (except for content pages and admin)
        if (!isScrolled) {
            return 'text-white';
        }
        
        // Default (scrolled state handles this with CSS)
        return darkMode ? 'dark-mode-icon' : '';
    };

    // Determine navbar class based on scroll and dark mode states
    const navbarClass = `navbar navbar-expand-lg ${darkMode ? 'navbar-dark' : 'navbar-light'} fixed-top ${darkMode && !isScrolled ? 'dark-mode-transparent' : ''} ${isMenuOpen ? 'menu-open' : ''}`;

    // Toggle menu function
    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        // Prevent body scrolling when menu is open
        if (!isMenuOpen) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }
    };

    // Close menu if window is resized to desktop size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 991 && isMenuOpen) {
                setIsMenuOpen(false);
                document.body.classList.remove('menu-open');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            // Ensure body scroll is restored when component unmounts
            document.body.classList.remove('menu-open');
        };
    }, [isMenuOpen]);

    return (
        <div>
            {/* Navigation Bar */}
            <nav className={navbarClass}>
                <div className="container">
                    <Link className="navbar-brand" to="/" onClick={scrollToTop}>
                        <img 
                            src="/assets/Images/logo.png" 
                            alt="LSQStays Logo" 
                            className="logo-image" 
                            height="50" 
                        />
                        <span className="logo-text">lsqstays</span>
                    </Link>
                    <div className="mobile-nav-controls">
                        {/* Moved dark mode toggle next to menu icon */}
                        <button 
                            className={`nav-link dark-mode-toggle mobile-dark-toggle ${getTextColorClass()}`} 
                            onClick={toggleDarkMode}
                            title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        >
                            <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'} ${getTextColorClass()}`} />
                        </button>
                        <button 
                            className={`navbar-toggler ${isMenuOpen ? 'active' : ''}`} 
                            type="button" 
                            onClick={toggleMenu} 
                            aria-expanded={isMenuOpen}
                            aria-label="Toggle navigation"
                        >
                            {/* Replace the default navbar-toggler-icon with a Font Awesome icon */}
                            <i className={`fas fa-bars menu-icon ${getTextColorClass()}`}></i>
                        </button>
                    </div>
                    <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarNav">
                        <ul className="navbar-nav mx-auto">
                            <li className="nav-item">
                                <Link 
                                    className={`nav-link ${currentPage === 'home' ? 'active' : ''} ${getTextColorClass()}`} 
                                    to="/" 
                                    onClick={handleNavLinkClick}
                                >
                                    Home
                                </Link>
                            </li>
                            <li className="nav-item dropdown">
                                <a className={`nav-link d-flex align-items-center ${currentPage === 'explore' ? 'active' : ''} ${getTextColorClass()}`} href="#">
                                    Explore <i className={`fas fa-chevron-down ms-1 ${getTextColorClass()}`} />
                                </a>
                                <ul className="dropdown-menu">
                                    {loading ? (
                                        <li><span className="dropdown-item">Loading locations...</span></li>
                                    ) : locations.length > 0 ? (
                                        locations.map((location, index) => (
                                            <li key={index}>
                                                <Link 
                                                    className="dropdown-item" 
                                                    to={`/properties?location=${encodeURIComponent(location)}`}
                                                    onClick={handleNavLinkClick}
                                                >
                                                    {location}
                                                </Link>
                                            </li>
                                        ))
                                    ) : (
                                        <li><span className="dropdown-item">No locations found</span></li>
                                    )}
                                    <li><hr className="dropdown-divider" /></li>
                                    <li>
                                        <Link 
                                            className="dropdown-item" 
                                            to="/properties"
                                            onClick={handleNavLinkClick}
                                        >
                                            View All Locations
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            <li className="nav-item">
                                <Link 
                                    className={`nav-link ${currentPage === 'about' ? 'active' : ''} ${getTextColorClass()}`} 
                                    to="/about"
                                    onClick={handleNavLinkClick}
                                >
                                    About
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link 
                                    className={`nav-link ${currentPage === 'contact' ? 'active' : ''} ${getTextColorClass()}`} 
                                    to="/contact"
                                    onClick={handleNavLinkClick}
                                >
                                    Contact
                                </Link>
                            </li>
                        </ul>
                        <div className="navbar-nav ms-auto">
                            <Link 
                                className={`nav-link ${currentPage === 'properties' ? 'active' : ''} ${getTextColorClass()}`} 
                                to="/properties"
                                onClick={handleNavLinkClick}
                            >
                                <i className={`fas fa-home ${getTextColorClass()}`} /> Properties
                            </Link>
                            {/* Desktop dark mode toggle - hidden on mobile */}
                            <button 
                                className={`nav-link dark-mode-toggle desktop-dark-toggle ${getTextColorClass()}`} 
                                onClick={toggleDarkMode}
                                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                            >
                                <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'} ${getTextColorClass()}`} />
                            </button>
                            <Link 
                                className={`nav-link ${currentPage === 'admin' ? 'active' : ''} ${getTextColorClass()}`} 
                                to="/admin" 
                                title="Admin Panel"
                                onClick={handleNavLinkClick}
                            >
                                <i className={`fas fa-user ${getTextColorClass()}`} />
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
            {/* Mobile menu overlay */}
            {isMenuOpen && <div className="mobile-menu-overlay" onClick={toggleMenu}></div>}
        </div>
    );
}

export default Header;